import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemIcon} from '../../../../components/common/dropdowns/dropdownItemIcon';
import type {MultipleGroupsDropdownBoxProps} from './multipleGroupsDropdownBox';
import type {DropdownGroup} from './multipleGroupsDropdownHelpers';

/*
 * Props.
 */

interface MultipleGroupsDropdownGroupProps<T> {
  canSelectMultipleItems?: boolean;
  selectedItems: MultipleGroupsDropdownBoxProps<T>['selectedItems'];
  group: DropdownGroup<T>;
  onChange: HandlerOf<T>;
}

/*
 * Style.
 */

export const GroupTitle = styled(DropdownItem)`
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade40};

  &:hover {
    background: none;
  }
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 4px;
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  align-items: center;
`;

export const StyledDropdownGroupTitleContent = styled(DropdownItemContent)`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const MultipleGroupsDropdownGroup = <T,>(
  props: MultipleGroupsDropdownGroupProps<T>,
): ReturnType<FC> => {
  const theme = useUserTheme();
  const {canSelectMultipleItems, selectedItems, group, onChange} = props;

  return (
    <>
      {group.title ? (
        <GroupTitle key={`group-${group.getItemKey(group.items[0])}`} isNotInteractive>
          {group.icon ? (
            <DropdownItemIcon color={theme.palette.teal.shade40} label={group.icon}>
              {group.icon}
            </DropdownItemIcon>
          ) : null}
          <StyledDropdownGroupTitleContent>{group.title}</StyledDropdownGroupTitleContent>
        </GroupTitle>
      ) : null}
      {group.render
        ? group.render()
        : group.items.map((item) => {
            const isChecked = Boolean(selectedItems?.includes(item));
            const isDisabled = Boolean(group.disabledItems?.includes(item));
            const itemTooltip = group.renderItemTooltip?.(item);

            return (
              <DropdownItem
                key={group.getItemKey(item)}
                renderRightContent={
                  canSelectMultipleItems ? () => <DropdownItemCheckbox isChecked={isChecked} /> : undefined
                }
                isChecked={isChecked}
                isDisabled={isDisabled}
                onSelect={() => onChange(item)}
              >
                {group.renderItemIcon ? (
                  <DropdownItemIcon color={theme.palette.teal.shade40} label={group.renderItemLabel(item)}>
                    {group.renderItemIcon(item)}
                  </DropdownItemIcon>
                ) : null}
                <StyledDropdownItemContent isChecked={isChecked}>
                  {group.renderItemLabel(item)}
                  {itemTooltip && (
                    <StyledTooltipCoordinator
                      iconColor={theme.greys.shade70}
                      render={() => itemTooltip}
                      placement="top"
                    />
                  )}
                </StyledDropdownItemContent>
              </DropdownItem>
            );
          })}
    </>
  );
};
